import React, { useState, useEffect, useCallback } from 'react';
import Login from './components/Login';
import ProfileForm from './components/ProfileForm';
import NewsDashboard from './components/NewsDashboard';
import SnapshotHistory from './components/SnapshotHistory';
import SnapshotDetail from './components/SnapshotDetail';
import TemplateEditor from './components/TemplateEditor';
import { getCurrentUser, signOut } from './utils/auth';
import { getProfiles, deleteProfile, refreshNews } from './utils/api';

function App() {
  const [authed, setAuthed] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [dashboards, setDashboards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('dashboards'); // 'dashboards' | 'history'
  const [viewSnapshotId, setViewSnapshotId] = useState(null);

  const loadProfiles = useCallback(async () => {
    try {
      const profiles = await getProfiles();
      setDashboards(profiles);
    } catch (err) {
      console.error('Failed to load profiles:', err);
    }
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user) {
      setAuthChecking(false);
      return;
    }
    user.getSession((err, session) => {
      if (!err && session?.isValid()) setAuthed(true);
      setAuthChecking(false);
    });
  }, []);

  // Recover gracefully when a token expires or a refresh fails mid-session:
  // drop back to the login screen instead of leaving stale error text on the page.
  useEffect(() => {
    const onExpired = () => {
      setAuthed(false);
      setDashboards([]);
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  useEffect(() => {
    if (authed) loadProfiles();
  }, [authed, loadProfiles]);

  const handleLogin = () => setAuthed(true);

  const handleLogout = () => {
    signOut();
    setAuthed(false);
    setDashboards([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dashboard?')) return;
    await deleteProfile(id);
    loadProfiles();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshNews();
      alert('RSS refresh triggered. New items will appear in ~30 seconds.');
    } catch (err) {
      alert('Refresh failed: ' + err.message);
    }
    setRefreshing(false);
  };

  if (authChecking) {
    return (
      <div style={{ maxWidth: 960, margin: '80px auto', padding: 24, textAlign: 'center', color: '#666', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
        Loading…
      </div>
    );
  }

  if (!authed) return <Login onLogin={handleLogin} />;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, borderBottom: '2px solid #232f3e', paddingBottom: 12 }}>
        <h1 style={{ margin: 0, color: '#232f3e' }}>📰 AWS News Curator</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setTab('dashboards'); setViewSnapshotId(null); }}
            style={{ padding: '6px 12px', background: tab === 'dashboards' ? '#0073bb' : '#e0e0e0', color: tab === 'dashboards' ? '#fff' : '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
            📰 Dashboards
          </button>
          <button onClick={() => { setTab('history'); setViewSnapshotId(null); }}
            style={{ padding: '6px 12px', background: tab === 'history' ? '#0073bb' : '#e0e0e0', color: tab === 'history' ? '#fff' : '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
            📋 History
          </button>
          <button onClick={() => setTab('templates')}
            style={{ padding: '6px 12px', background: tab === 'templates' ? '#0073bb' : '#e0e0e0', color: tab === 'templates' ? '#fff' : '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
            ✉️ Templates
          </button>
          <button onClick={handleRefresh} disabled={refreshing}
            style={{ padding: '6px 12px', background: '#ec7211', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
            {refreshing ? 'Refreshing...' : '🔄 Refresh RSS'}
          </button>
          <button onClick={() => { setTab('dashboards'); setShowForm(true); }}
            style={{ padding: '6px 12px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
            ➕ New Dashboard
          </button>
          <button onClick={handleLogout}
            style={{ padding: '6px 12px', background: '#545b64', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
            Sign Out
          </button>
        </div>
      </div>

      {tab === 'history' && viewSnapshotId && (
        <SnapshotDetail snapshotId={viewSnapshotId} onBack={() => setViewSnapshotId(null)} />
      )}

      {tab === 'history' && !viewSnapshotId && (
        <SnapshotHistory onView={setViewSnapshotId} />
      )}

      {tab === 'templates' && <TemplateEditor />}

      {tab === 'dashboards' && (
        <>
          {showForm && <ProfileForm onCreated={() => { setShowForm(false); loadProfiles(); }} />}

          {dashboards.length === 0 && !showForm && (
            <div style={{ textAlign: 'center', padding: 48, color: '#666' }}>
              <p style={{ fontSize: 18 }}>No dashboards yet.</p>
              <p>Create a dashboard profile to start getting AI-curated AWS news tailored to your audience.</p>
            </div>
          )}

          {dashboards.map((d) => (
            <NewsDashboard key={d.dashboardId || d.SK} dashboard={d} onDelete={handleDelete} />
          ))}
        </>
      )}
    </div>
  );
}

export default App;
